//@flow
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {Link, useLocation} from 'react-router-dom';
import {
  Chip,
  Grid,
  Hidden,
  IconButton,
  List,
  ListItem,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {
  AddCircleOutline,
  ArrowBack,
  Edit,
  InfoOutlined,
  NotificationsNoneOutlined,
  PlayCircleFilledTwoTone,
} from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import Skeleton from '@material-ui/lab/Skeleton';
import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';

import {usePage} from '../../page/utils';

import {colors} from '../../../assets/styles/colors';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import sortIcon from '../../../assets/img/sort-icon.svg';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomerCategoryModal from './CustomerCategoryModal/CustomerCategoryModal';
import SearchBar from '../../../elements/SearchBar/SearchBar';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import CustomerItem from './CustomerItem/CustomerItem';
import CustomCategoryChip from './ColorsPalette/CustomCategoryChip';
import CustomTextEditor from '../../../elements/CustomTextEditor/CustomTextEditor';
import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
// import crmWssp from '../../../assets/img/crm-img.png';

import {generateWhatsappLink, replaceTemplate} from '../../../utils/share';
import {useIsFeatureInTier} from '../../../utils/component';
import {endpoints} from '../api';

import type {
  Store,
  User,
  Currency,
  CrmCustomer,
  CustomerCategory,
} from '../../../entities';

import type {CategoryRequest} from '../api';
import type {ICommand, TextState, TextAreaTextApi} from '@uiw/react-md-editor';
import {useIsMinimalPlan} from '../../store/utils';
import {useMinimalTierContext} from '../../../context/MinimalProvider';
import CrmDisabledModal from './CrmDisabledModal/CrmDisabledModal';
import PremiumFeatureBadge from '../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {TIENDADA_LINKS} from '../../../constants';

type Props = {
  currency?: Currency,
  user?: User,
  store?: Store,
  categories?: CustomerCategory[],
  customers?: CrmCustomer[],
  pagination?: Pagination,
  isSaving?: boolean,
  createCategorySuccess?: boolean,
  editCustomerSuccess?: boolean,
  customerError?: boolean,
  categoryError?: string,
  fetchCustomersError?: boolean,
  message?: string,
  isFetchingMessage?: boolean,
  isSavingMessage?: boolean,
  saveMessageSuccess?: boolean,
  editCategorySuccess?: boolean,
  deleteCategorySuccess?: boolean,
  resetPartialState: () => void,
  fetchCustomers: ({queryFilter?: {[key: string]: string}}) => void,
  fetchCustomerCategories: () => void,
  editCustomerCategory: ({categoryRequest: CategoryRequest}) => void,
  deleteCustomerCategory: ({categoryRequest: CategoryRequest}) => void,
  fetchCustomerMessage: () => void,
  saveCustomerMessage: ({message: string}) => void,
  isFetchingCustomers?: boolean,
  createCustomerCategory: ({categoryRequest: CategoryRequest}) => void,
  editCustomer: ({[key: string]: string}) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#f8f9fb',
  },
  headerWrapper: {
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      background: 'white',
      position: 'sticky',
      top: 72,
      zIndex: 3,
      padding: theme.spacing(1, 4),
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 2),
      },
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    '& .MuiButton-label': {
      gap: '8px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
    // [theme.breakpoints.up('md')]: {
    //   pointerEvents: 'none',
    // },
  },
  boldTitle: {
    color: '#3A3A49',
    fontSize: '14px',
    fontWeight: 600,
  },
  whiteCard: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    height: '100%',
  },
  gridContent: {
    margin: theme.spacing(2, 0),
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 4, 0, 4),
    marginBottom: 64,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 180,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 0, 2),
      marginBottom: 220,
    },
  },
  btnHeader: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      paddingLeft: 5,
      boxShadow: 'unset',
    },
  },
  mobileContent: {
    margin: theme.spacing(2),
  },
  mobileText: {
    fontSize: '14px',
    color: 'rgb(113, 113, 144)',
  },
  rowContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  categoryButton: {
    height: 32,
    fontSize: '0.875rem',
    fontWeight: 500,
    marginRight: 12,
  },
  containerButtonSendTest: {
    marginTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  containerCategorys: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    gap: '4px',
  },
  wrapFilter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 0, 1, 0),
  },
  filterRight: {
    display: 'flex',
    alignItems: 'center',
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  textFilter: {
    fontSize: '12px',
    fontWeight: '500',
    color: colors.grey900,
    marginRight: theme.spacing(1),
  },
  textGray: {
    color: colors.grey700,
    fontSize: '14px',
  },
  gridUpsell: {
    marginTop: theme.spacing(2),
  },
  upsellMessage: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
      flexWrap: 'wrap',
    },
    color: colors.purple3,
    background: colors.lightPurple,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    backgroundColor: colors.grey200,
    borderRadius: '8px',
  },
  headerColumn: {
    fontSize: '13px',
    fontWeight: 500,
    [theme.breakpoints.up('xl')]: {
      fontSize: '14px',
    },
  },
  wrapColumn: {
    flex: '2 1',
    display: 'flex',
    // justifyContent: 'center',
    alignItems: 'center',
  },
  listItem: {
    padding: 0,
    borderBottom: '1px solid #F4F4F6',
  },
  bgBlurry: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 10,
    width: '100%',
    height: '100%',
    background: '#ffffff10',
    backdropFilter: 'blur(1px)',
  },
  customerList: {
    paddingTop: 0,
    padding: 0,
  },
  descriptionWrap: {
    '& .MuiFilledInput-multiline': {
      padding: '0 0 48px 0',
    },
  },
  containerRichText: {
    position: 'relative',
    margin: theme.spacing(2, 0, 1, 0),
  },
  actionTextEditor: {
    position: 'absolute',
    right: theme.spacing(2),
    bottom: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
  },
  actionButton: {
    padding: '5px 15px',
    fontSize: '12px',
    color: '#7B39FF',
    border: '1px solid #7B39FF',
    fontFamily: 'Poppins',
    fontWeight: 600,
    borderRadius: '20px',
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  pagination: {
    margin: theme.spacing(2, 0),
    '& .Mui-selected': {
      backgroundColor: colors.lightPurple,
      color: colors.purple3,
    },
    '& .Mui-selected:hover': {
      backgroundColor: colors.lightPurple,
      // color: colors.purple3,
    },
  },
  withoutCustomersText: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  imgCrmWssp: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export default usePage<Props>(Customers, 'Customers');

function Customers(props: Props) {
  const {
    store,
    user,
    currency,
    customers,
    categories,
    pagination,
    isSaving,
    isFetchingCustomers,
    categoryError,
    editCustomerSuccess,
    customerError,
    fetchCustomersError,
    createCategorySuccess,
    fetchCustomerCategories,
    fetchCustomers,
    fetchCustomerMessage,
    saveCustomerMessage,
    message,
    saveMessageSuccess,
    editCategorySuccess,
    deleteCategorySuccess,
    editCustomerCategory,
    deleteCustomerCategory,
    isSavingMessage,
    createCustomerCategory,
    editCustomer,
    resetPartialState,
  } = props;

  const classes = useStyles();
  const location = useLocation();

  const [orderBy, setOrderBy] = useState(undefined);
  const [sortBy, setSortBy] = useState(undefined);
  const [messageText, setMessageText] = useState('');
  const [filterCategory, setFilterCategory] = useState('--');
  const [openCategoryModal, setOpenCategoryModal] = useState(false);

  const isMinimal = useIsMinimalPlan();

  const {
    openRestriction,
    handleCloseRestriction,
    handleOpenRestriction,
  } = useMinimalTierContext();

  const getSearchFromQuery = (search: string) => {
    const s = queryString.parse(search);
    return s;
  };
  const query = getSearchFromQuery(location.search);
  const p = parseInt(query.page, 10);
  const s = parseInt(query.size, 10);
  const [page, setPage] = useState(
    isNaN(p) ? (pagination ? pagination.page + 1 : 1) : p
  );
  const [size, setSize] = useState(
    isNaN(s) ? (pagination ? pagination.size : 16) : s
  );
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handleSizeChange = event => {
    if (event) {
      console.log(event.target.value);
      setSize(event.target.value);
    }
  };

  const handleOpenCategoryModal = () => setOpenCategoryModal(true);
  const handleCloseCategoryModal = () => setOpenCategoryModal(false);

  const customCommands: ICommand[] = [
    {
      name: 'Customer name',
      enabled: true,
      keyCommand: 'nombre_cliente',
      buttonProps: {'aria-label': 'Insert customer name'},
      icon: <div className={classes.actionButton}>Nombre de cliente</div>,
      execute: (state: TextState, api: TextAreaTextApi) => {
        api.replaceSelection('{{nombre_cliente}}');
      },
    },
    {
      name: 'Store name',
      enabled: true,
      keyCommand: 'nombre_tienda',
      buttonProps: {'aria-label': 'Insert store name'},
      icon: <div className={classes.actionButton}>Nombre de tienda</div>,
      execute: (state: TextState, api: TextAreaTextApi) => {
        api.replaceSelection('{{nombre_tienda}}');
      },
    },
    {
      name: 'Store url',
      enabled: true,
      keyCommand: 'url_tienda',
      buttonProps: {'aria-label': 'Insert store url'},
      icon: <div className={classes.actionButton}>Link de tienda</div>,
      execute: (state: TextState, api: TextAreaTextApi) => {
        api.replaceSelection('{{link_tienda}}');
      },
    },
  ];

  const handleSendMessage = () => {
    const messageToSend = replaceTemplate(messageText, {
      nombre_tienda: store ? store.name : '',
      nombre_cliente: user ? user.firstName : '',
      link_tienda:
        store && store.domains && store.domains[0].domainName
          ? store.domains[0].domainName
          : '',
    });
    const wsspMessage = generateWhatsappLink(
      user ? user.phone : '',
      messageToSend
    );
    if (window) window.open(wsspMessage, '_blank', 'noopener,noreferrer');
  };

  const handleSortBy = (sortKey: string) => {
    if (sortBy !== sortKey) {
      setSortBy(sortKey);
      setOrderBy('ASC');
    } else if (sortBy) {
      setOrderBy(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    }
  };

  const handleSaveCustomMessage = () => {
    if (!messageText.trim().length) return;
    saveCustomerMessage({message: messageText});
  };

  const handleFilterChange = e => {
    if (e) {
      setFilterCategory(e.target.value);
    }
  };
  const buildQueryParamsCallback = useCallback(
    (searchString?: string) => {
      const params: {[key: string]: any} = {
        size: size,
        page: page - 1,
        sort: sortBy,
        order: orderBy,
      };
      if (searchString) {
        params.fullName = searchString;
      }
      if (filterCategory !== '--') {
        params.categoryId = filterCategory;
      }
      return params;
    },
    [size, page, sortBy, orderBy, filterCategory]
  );

  const hasCustomerFeature = useIsFeatureInTier('CRM_CUSTOMER');

  const shouldDisabledTier = !isMinimal && !hasCustomerFeature;

  useEffect(() => {
    if (message) {
      setMessageText(message);
    }
  }, [message]);

  const fetchCustomersCallback = useCallback(
    (searchString?: string) => {
      const queryFilter = buildQueryParamsCallback(searchString);
      if (queryFilter && hasCustomerFeature) {
        fetchCustomers({queryFilter});
      }
    },
    [buildQueryParamsCallback, fetchCustomers, hasCustomerFeature]
  );

  useEffect(() => {
    if (hasCustomerFeature) fetchCustomersCallback();
  }, [fetchCustomersCallback, hasCustomerFeature]);

  useEffect(() => {
    if (hasCustomerFeature) {
      fetchCustomerMessage();
      fetchCustomerCategories();
    }
  }, [fetchCustomerCategories, fetchCustomerMessage, hasCustomerFeature]);

  useEffect(() => {
    return () => resetPartialState();
  }, [resetPartialState]);

  useEffect(() => {
    handleOpenRestriction();
  }, [handleOpenRestriction]);

  const paginationArea = (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <option value={8}>8 por página</option>
          <option value={16}>16 por página</option>
          <option value={24}>24 por página</option>
          <option value={32}>32 por página</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <MenuItem value={8}>8 por página</MenuItem>
          <MenuItem value={16}>16 por página</MenuItem>
          <MenuItem value={24}>24 por página</MenuItem>
          <MenuItem value={32}>32 por página</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );

  return (
    <div className={classes.root}>
      <SnackBar
        mode="success"
        message={createCategorySuccess ? 'Categoría creada' : undefined}
      />
      <SnackBar
        mode="success"
        message={editCategorySuccess ? 'Categoría actualizada' : undefined}
      />
      <SnackBar
        mode="success"
        message={deleteCategorySuccess ? 'Categoría eliminada' : undefined}
      />
      <SnackBar
        mode="success"
        message={editCustomerSuccess ? 'Cliente actualizado' : undefined}
      />
      <SnackBar
        mode="success"
        message={saveMessageSuccess ? 'Mensaje actualizado' : undefined}
      />
      <SnackBar
        mode="error"
        message={!!customerError ? 'Error al actualizar cliente' : undefined}
      />
      <SnackBar
        mode="error"
        message={
          !!fetchCustomersError ? 'Error al obtener los clientes' : undefined
        }
      />
      <SnackBar
        mode="error"
        message={
          !!categoryError ? 'Error al actualizar la categoría' : undefined
        }
      />
      {isSavingMessage && <LoadingSpinner />}
      <CrmDisabledModal
        open={openRestriction}
        handleClose={handleCloseRestriction}
      />

      <div className={classes.headerWrapper}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <Hidden mdUp>
              <CustomButton className={classes.title} to="/client">
                <ArrowBack />
                <div style={{display: 'flex', alignItems: 'center'}}>
                  Clientes
                  {store && !hasCustomerFeature && (
                    <Chip
                      label="PRO"
                      size="small"
                      className={classes.newLabel}
                    />
                  )}
                </div>
              </CustomButton>
            </Hidden>
            <Hidden smDown>
              <div
                className={classes.title}
                style={{display: 'flex', alignItems: 'center'}}
              >
                Clientes
                {store && !hasCustomerFeature && <PremiumFeatureBadge />}
                {isMinimal && (
                  <IconButton onClick={handleOpenRestriction}>
                    <InfoOutlined />
                  </IconButton>
                )}
              </div>
            </Hidden>
          </Grid>
          <Grid item>
            <CustomButton
              component={Link}
              to="/tiers-list"
              className={classes.btnHeader}
              style={{marginRight: '8px'}}
            >
              Mejorar Plan
            </CustomButton>
            <CustomButton
              href={TIENDADA_LINKS.tutorials.CRM}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.btnHeader}
              startIcon={<PlayCircleFilledTwoTone />}
            >
              Tutoriales
            </CustomButton>
          </Grid>
        </Grid>
      </div>

      <div className={classes.content}>
        <Hidden smDown>
          <Grid container className={classes.gridUpsell}>
            {shouldDisabledTier && (
              <div className={classes.upsellMessage}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <InfoOutlined style={{marginRight: 16}} />
                  Suscríbete a nuestros planes para acceder a funcionalidades
                  PRO e impulsar tu negocio
                </div>
                <CustomButton
                  to="/tiers-list"
                  className={classes.upgradeTierButton}
                >
                  Mejorar Plan
                </CustomButton>
              </div>
            )}
          </Grid>
          <Grid container spacing={2} className={classes.gridContent}>
            <Grid item xs={8}>
              <div className={classes.whiteCard}>
                <div className={classes.cardContent}>
                  <div className={classes.rowContent}>
                    <Typography className={classes.boldTitle}>
                      1. Mantén una relación con tus clientes escribiendo un
                      mensaje
                    </Typography>
                  </div>
                  <Typography className={classes.textGray}>
                    Envía un mensaje por WhatsApp. Puedes incluir una promoción
                    o tan solo un saludo.
                  </Typography>
                  <div className={classes.containerRichText}>
                    <CustomTextEditor
                      disabled={!hasCustomerFeature || isSavingMessage}
                      text={messageText}
                      placeholder="Detallar especificaciones adicionales"
                      extraCommands={customCommands}
                      onChange={str => setMessageText(str)}
                    />
                  </div>
                  <div className={classes.containerButtonSendTest}>
                    <CustomButton
                      onClick={handleSendMessage}
                      disabled={!hasCustomerFeature}
                    >
                      Enviarme una prueba
                    </CustomButton>
                    <CustomButton
                      onClick={handleSaveCustomMessage}
                      disabled={!hasCustomerFeature || isSavingMessage}
                      variant="contained"
                    >
                      Guardar
                    </CustomButton>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={classes.whiteCard}>
                <div className={classes.cardContent}>
                  <div className={classes.rowContent}>
                    <Typography className={classes.boldTitle}>
                      Categorias clientes
                    </Typography>
                  </div>

                  <CustomButton
                    variant="contained"
                    className={classes.categoryButton}
                    startIcon={
                      categories && categories.length > 0 ? (
                        <Edit />
                      ) : (
                        <AddCircleOutline />
                      )
                    }
                    disabled={!hasCustomerFeature}
                    onClick={handleOpenCategoryModal}
                  >
                    {categories && categories.length > 0
                      ? 'Editar Categorías'
                      : 'Crear Categoría'}
                  </CustomButton>
                  <div className={classes.containerCategorys}>
                    {categories
                      ? categories.map((c, idx) => (
                          <CustomCategoryChip
                            category={c}
                            key={`category-chip-${idx}`}
                          />
                        ))
                      : null}
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.whiteCard}>
                <div className={classes.cardContent}>
                  <div className={classes.rowContent}>
                    <Typography className={classes.boldTitle}>
                      2. Presionar icono de WhatsApp para enviar mensaje
                    </Typography>
                  </div>
                  <div className={classes.wrapFilter}>
                    <SearchBar
                      placeholder="Buscar cliente"
                      maxWidth={440}
                      externalHandleSearch={fetchCustomersCallback}
                    />
                    <div className={classes.filterRight}>
                      <Typography className={classes.textFilter}>
                        Filtrar por:
                      </Typography>
                      <CustomSelect
                        disabled={!hasCustomerFeature}
                        pagination
                        disableUnderline
                        value={filterCategory}
                        onChange={handleFilterChange}
                      >
                        <MenuItem aria-label="None" value={'--'}>
                          Ver todos
                        </MenuItem>
                        {categories &&
                          categories.map((cat, idx) => (
                            <MenuItem
                              key={`opt-cat-${idx}`}
                              value={cat.id}
                              // className={classes.itemSelect}
                            >
                              {cat.name}
                            </MenuItem>
                          ))}
                      </CustomSelect>
                      <CustomButton
                        disabled={!hasCustomerFeature}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${endpoints.EXPORT_CUSTOMERS}?format=XLSX`}
                      >
                        <SaveAltRoundedIcon color="primary" />
                      </CustomButton>
                    </div>
                  </div>
                  <div style={{position: 'relative', height: 'fit-content'}}>
                    {isMinimal && <div className={classes.bgBlurry} />}
                    <div className={classes.tableHeader}>
                      <div className={classes.wrapColumn}>
                        <Typography className={classes.headerColumn}>
                          Nombre del Cliente
                        </Typography>
                        <IconButton onClick={() => handleSortBy('FULL_NAME')}>
                          <img src={sortIcon} alt="sort by" />
                        </IconButton>
                      </div>
                      <div className={classes.wrapColumn}>
                        <Typography className={classes.headerColumn}>
                          Última compra
                        </Typography>
                        <IconButton
                          onClick={() => handleSortBy('LAST_ORDER_DATE')}
                        >
                          <img src={sortIcon} alt="sort by" />
                        </IconButton>
                      </div>
                      <div className={classes.wrapColumn}>
                        <Typography className={classes.headerColumn}>
                          Monto promedio
                        </Typography>
                        <IconButton
                          onClick={() => handleSortBy('AVERAGE_ORDER_AMOUNT')}
                        >
                          <img src={sortIcon} alt="sort by" />
                        </IconButton>
                      </div>
                      <div className={classes.wrapColumn}>
                        <Typography className={classes.headerColumn}>
                          Categoría
                        </Typography>
                        <IconButton onClick={() => handleSortBy('CATEGORY')}>
                          <img src={sortIcon} alt="sort by" />
                        </IconButton>
                      </div>
                      <div className={classes.wrapColumn}>
                        <Typography className={classes.headerColumn}>
                          Pedidos Hechos
                        </Typography>
                        <IconButton
                          onClick={() => handleSortBy('NUMBER_OF_ORDERS')}
                        >
                          <img src={sortIcon} alt="sort by" />
                        </IconButton>
                      </div>
                      <div style={{width: 135}} />
                    </div>
                    <List className={classes.customerList}>
                      {isFetchingCustomers && !customers
                        ? Array.from({length: 3}, (x, i) => (
                            <ListItem key={`skelly-${i}`}>
                              <Skeleton
                                variant="rect"
                                height="74px"
                                width="100%"
                              />
                            </ListItem>
                          ))
                        : customers &&
                          customers.map((c: CrmCustomer, idx) => (
                            <div key={`customer-${idx}`}>
                              <ListItem className={classes.listItem}>
                                <CustomerItem
                                  store={store}
                                  message={message}
                                  isSaving={isSaving}
                                  editCustomer={editCustomer}
                                  categories={categories}
                                  currency={currency}
                                  customer={c}
                                />
                              </ListItem>
                            </div>
                          ))}
                    </List>
                    {customers && !customers.length ? (
                      <div className={classes.withoutCustomersText}>
                        No se encontraron clientes
                      </div>
                    ) : null}
                    {paginationArea}
                  </div>
                </div>
              </div>
            </Grid>
            {/* <Grid item xs={12}>
              <img
                src={crmWssp}
                alt="crm wssp"
                className={classes.imgCrmWssp}
              />
            </Grid> */}
          </Grid>
        </Hidden>
        <Hidden mdUp>
          <div className={classes.mobileContent}>
            <div className={classes.whiteCard}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#717190',
                  justifyContent: 'center',
                }}
              >
                <IconButton style={{color: '#717190'}}>
                  <NotificationsNoneOutlined />
                </IconButton>
                <Typography className={classes.mobileText}>
                  Para ver el detalle de tus clientes, accede desde una
                  computadora.
                </Typography>
              </div>
            </div>
          </div>
        </Hidden>
      </div>
      {openCategoryModal && (
        <CustomerCategoryModal
          open={openCategoryModal}
          handleClose={handleCloseCategoryModal}
          categories={categories}
          createCategory={createCustomerCategory}
          createCategorySuccess={createCategorySuccess}
          editCategorySuccess={editCategorySuccess}
          deleteCategorySuccess={deleteCategorySuccess}
          deleteCategory={deleteCustomerCategory}
          editCategory={editCustomerCategory}
        />
      )}
    </div>
  );
}

Customers.propTypes = {
  store: PropTypes.object,
  currency: PropTypes.object,
  user: PropTypes.object,
  customers: PropTypes.array,
  categories: PropTypes.array,
  pagination: PropTypes.object,
  isFetchingCustomers: PropTypes.bool,
  createCategorySuccess: PropTypes.bool,
  categoryError: PropTypes.string,
  editCustomerSuccess: PropTypes.bool,
  customerError: PropTypes.bool,
  isSaving: PropTypes.bool,
  fetchCustomersError: PropTypes.bool,
  message: PropTypes.string,
  isFetchingMessage: PropTypes.bool,
  isSavingMessage: PropTypes.bool,
  saveMessageSuccess: PropTypes.bool,
  editCategorySuccess: PropTypes.bool,
  deleteCategorySuccess: PropTypes.bool,
  resetPartialState: PropTypes.func.isRequired,
  fetchCustomers: PropTypes.func.isRequired,
  editCustomer: PropTypes.func.isRequired,
  fetchCustomerCategories: PropTypes.func.isRequired,
  createCustomerCategory: PropTypes.func.isRequired,
  editCustomerCategory: PropTypes.func.isRequired,
  fetchCustomerMessage: PropTypes.func.isRequired,
  saveCustomerMessage: PropTypes.func.isRequired,
  deleteCustomerCategory: PropTypes.func.isRequired,
};
